import { Icon } from '@iconify/react'
import { animated, config, to, useSpring, useTransition } from '@react-spring/web'
import { Billboard, Html, type BillboardProps } from '@react-three/drei'
import clsx from 'clsx'
import { forwardRef, useState } from 'react'

export type UncoupleIndicatorType = 'none' | 'allow' | 'uncoupled' | 'deny'

interface UncoupleIndicatorProps extends BillboardProps {
  type: UncoupleIndicatorType
  scale?: number
  onPointerOver?: () => void
  onPointerOut?: () => void
  onClick?: () => void
}

export const UncoupleIndicator = forwardRef<HTMLDivElement, UncoupleIndicatorProps>(
  ({ type, scale = 1, onPointerOver, onPointerOut, onClick, ...props }, ref) => {
    const [lastVisibleType, setLastVisibleType] = useState(type)
    if (type !== 'none' && type !== lastVisibleType) setLastVisibleType(type)

    const transitions = useTransition(type !== 'none' ? 'visible' : null, {
      from: { scale: 0 },
      enter: { scale: 1 },
      leave: { scale: 0 },
      config: config.wobbly,
    })

    const spring = useSpring({
      scale,
      config: config.wobbly,
    })

    const displayType = type !== 'none' ? type : lastVisibleType

    return (
      <Billboard {...props}>
        <Html transform scale={0.4} ref={ref} style={{ pointerEvents: onClick ? 'auto' : 'none' }}>
          {transitions(
            (style, item) =>
              item && (
                <animated.div
                  style={{
                    ...style,
                    scale: to(
                      [style.scale, spring.scale],
                      (entryScale, liveScale) => entryScale * liveScale,
                    ),
                    display: 'inline-flex',
                    cursor: onClick ? 'pointer' : 'default',
                  }}
                  onPointerOver={onPointerOver}
                  onPointerOut={onPointerOut}
                  onClick={onClick}
                >
                  {displayType === 'allow' && (
                    <div
                      className={clsx(
                        'flex size-10 items-center justify-center rounded-full bg-radial from-sky-500 to-sky-800',
                      )}
                    >
                      <Icon icon="noto:link" width="32" height="32" />
                    </div>
                  )}

                  {displayType === 'deny' && <Icon icon="noto:cross-mark" width="32" height="32" />}

                  {displayType === 'uncoupled' && (
                    <div
                      className={clsx(
                        'flex size-10 items-center justify-center rounded-full bg-radial from-yellow-500 to-yellow-800',
                      )}
                    >
                      <Icon icon="noto:broken-chain" width="32" height="32" />
                    </div>
                  )}
                </animated.div>
              ),
          )}
        </Html>
      </Billboard>
    )
  },
)
